import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import BeatLoader from 'react-spinners/BeatLoader'
import { AssignmentResponse, ClassResponse, StudentResponse } from "shared/types/teacherTypes"
import { assignmentsTopicWeights, getGradingPeriods, getStudents } from "shared/routes/teacher"
import { useModel } from '@stem-sims/nexus'
import GradeBookTable from './GradeBookTable'
import { Col, Form, Pagination, Row } from 'react-bootstrap'
import { TopicAssignmentSectionWeights } from "shared/types/assignmentTypes"
import CreateGradingPeriod from "./CreateGradingPeriods"
import SelectGradingPeriods from "./SelectGradingPeriods"
import { SELECTED_PERIODS_LOCAL_KEY } from "constants/generalConstants"
import { useParams } from "react-router-dom"
import { TextbookProductType } from "shared/types/curriculumTypes"
import { HomeSchoolBlock } from "shared/types/homeschoolTypes"
import { AuthContext } from "AuthContext"
import useValidateTextbookAccess from "hooks/useValidateTextbookAccess"
import getFilteredAssignments from "helpers/getFilteredAssignments"

export type StudentAssignmentGrade = {
    assignmentId: string
    graded: boolean
    late: boolean
    grade?: number
    submissionId?: string
}

interface GradeBookProps {
    activeClass: ClassResponse
    refreshClasses: () => void
}

export default function GradeBook({ activeClass, refreshClasses }: GradeBookProps) {
    const [students, setStudents] = useState<StudentResponse[]>([])
    const [loadingStudents, setLoading] = useState(false)
    const [startIndex, setStartIndex] = useState(0)
    const [count, setCount] = useState(5)
    const [assignmentSectionWeights, setAssignmentSectionWeights] = useState<TopicAssignmentSectionWeights[]>([])
    
    const { textbookProduct } = useParams() as { textbookProduct?: TextbookProductType }
    const { hasScience21Access, homeschoolBlocks } = React.useContext(AuthContext)

    const homeschoolBlock: HomeSchoolBlock | undefined = textbookProduct === "homeschool35" ? "Homeschool 35" : textbookProduct === "homeschool68" ? "Homeschool 68" : undefined

    useValidateTextbookAccess({ textbookProduct, hasScience21Access, homeschoolBlocks, homeschoolBlock })

    const savedPeriods: string[] | undefined = JSON.parse(localStorage.getItem(SELECTED_PERIODS_LOCAL_KEY))
    const [selectedPeriods, setSelectedPeriods] = useState<string[]>(savedPeriods ?? [])

    const { response: assignments, loading, refreshModel: reloadAssignments } = useModel({
        model: () => getFilteredAssignments({ classID: activeClass.id, textbookProduct, homeschoolBlocks: homeschoolBlock ? [homeschoolBlock] : undefined }),
        onError: (err) => {
            toast.error(err.response?.data?.message ?? "There has been an error loading the assignments. Please try again.")
            refreshClasses()
        },
        props: { classID: activeClass.id, selectedPeriodsIds: selectedPeriods },
        deps: [textbookProduct, homeschoolBlock]
    })

    const { response: gradingPeriods, loading: loadingGradingPeriods, refreshModel: refreshPeriods } = useModel({
        model: getGradingPeriods,
        props: { classId: activeClass.id }
    })

    /** Fetch topic assignments' section weights */
    useEffect(() => {
        if (!assignments || assignments.length === 0) return
        const topicAssignmentIds = assignments.filter(a => a.topicId).map(a => a.id) // get all topic assignment ids
        if (topicAssignmentIds.length === 0) return
        assignmentsTopicWeights({assignmentIds: topicAssignmentIds})
        .then(setAssignmentSectionWeights)
        .catch(() => toast.error("There was an error getting topic assignment weights."))
    }, [assignments])

    const sort = React.useRef<"first" | "last">(localStorage.getItem("studentSortOrder") as "first" | "last" ?? "last")
    const studentSort = (s1: StudentResponse, s2: StudentResponse) => {
        const firstStudentSort = s1.name.split(" ").at(sort.current === "first" ? 0 : -1)
        const secondStudentSort = s2.name.split(" ").at(sort.current === "first" ? 0 : -1)
        return firstStudentSort.localeCompare(secondStudentSort)
    }

    const refreshStudents = () => {
        setLoading(true)
        return getStudents({ classID: activeClass.id }).then((teacherStudents) => {
            setStudents(teacherStudents.sort(studentSort))
            setLoading(false)
        }).catch(() => {
            //Does the class still exist?
            toast.dismiss()
            toast.error("There was an issue getting your students.")
            refreshClasses()
        })
    }

    useEffect(() => {
        reloadAssignments()
    },
        //eslint-disable-next-line react-hooks/exhaustive-deps
        [activeClass.id])

    useEffect(() => {
        void refreshStudents()
    },
        //eslint-disable-next-line react-hooks/exhaustive-deps
        [activeClass.id])

    useEffect(() => {
        setStartIndex(0)
    },
        [assignments])

    useEffect(() => {
        if (startIndex % count !== 0) {
            setStartIndex(startIndex - (startIndex % count))
        }
    }, [count, startIndex])

     const studentGrades = useMemo(() => {
        if (loading || !assignments) return {}

        const grades: { [key: string]: StudentAssignmentGrade[] } = {}

        students.forEach(student => {
            grades[student.uuid] = []
            for (let i = 0; i < assignments.length; i++) {
                const assignment = assignments[i]
                // Get the most recent submission for the student
                const studentSubmission = assignment.submissions?.find(s => s.studentId === student.uuid && s.status === "graded")
                // If the student has not submitted the assignment, add a grade of 0 for the assignment only if late
                if (!studentSubmission) {
                    const ungradedSubmission = assignment.submissions?.find(s => s.studentId === student.uuid && s.status !== "incomplete")
                    if (ungradedSubmission) {
                        const late = assignment.dueDate && new Date(assignment.dueDate) < new Date(ungradedSubmission.lessonSubmittedDate)
                        grades[student.uuid].push({
                            assignmentId: assignment.id,
                            submissionId: ungradedSubmission.id,
                            graded: false,
                            late: late,
                            grade: late ? 0 : null
                        })
                    } else {
                        const late = assignment.dueDate && new Date(assignment.dueDate) < new Date()
                        grades[student.uuid].push({
                            assignmentId: assignment.id,
                            submissionId: null,
                            graded: false,
                            late: late,
                            grade: late ? 0 : null
                        })
                    }
                } else {
                    const late = assignment.dueDate && new Date(assignment.dueDate) < new Date(studentSubmission.lessonSubmittedDate)
                    // If it is graded, show the grade and whether it is late
                    if (studentSubmission.status === "graded") {
                        grades[student.uuid].push({
                            assignmentId: assignment.id,
                            submissionId: studentSubmission?.id,
                            graded: true,
                            late: late,
                            grade: studentSubmission?.grade
                        })
                    }
                }
            }
        })
        return grades
    }, [students, assignments, loading])

    /** Return true if a given topic assignment has 0 section weights sum */
    const isUnweightedAssignment = useCallback((assignmentId: string) => {
        const topicAssignmentWeights = assignmentSectionWeights.find(a => a.assignmentId === assignmentId)
        if (!topicAssignmentWeights) return false

        const weightSum = Object.keys(topicAssignmentWeights.sections).reduce((acc, key) => {
            acc += topicAssignmentWeights.sections[key]
            return acc
        }, 0)

        return weightSum === 0
    }, [assignmentSectionWeights])

    const calculateClassAverage = useCallback((assignment: AssignmentResponse) => {
        if (isUnweightedAssignment(assignment.id)) return ""
        let totalClassSum = 0
        let totalStudentSubmissions = 0

        for (const student of students) {
            const studentAssignmentGrades = studentGrades[student.uuid]
            for (const grade of studentAssignmentGrades) {
                if (grade.assignmentId !== assignment.id || grade.grade === null) continue
                totalClassSum += grade.grade ?? 0
                totalStudentSubmissions += 1
            }
        }
        if (totalStudentSubmissions === 0) return ""
        return `${(totalClassSum / totalStudentSubmissions).toFixed(2)}%`
    }, [students, studentGrades, isUnweightedAssignment])

    const calculateStudentClassGrade = useCallback((student: StudentResponse) => {
        let submissionCount = 0
        let studentGradeSum = 0

        const studentAssignmentGrades = studentGrades[student.uuid]

        for (const grade of studentAssignmentGrades) {
            if (grade.grade === null || isUnweightedAssignment(grade.assignmentId)) continue
            submissionCount += 1
            studentGradeSum += grade.grade
        }

        let gradeAvg = studentGradeSum
        if (submissionCount > 0) {
            gradeAvg = studentGradeSum / submissionCount
        }

        let gradeLetter: string
        if (gradeAvg >= 90) gradeLetter = 'A'
        else if (gradeAvg >= 80) gradeLetter = 'B'
        else if (gradeAvg >= 70) gradeLetter = 'C'
        else if (gradeAvg >= 60) gradeLetter = 'D'
        else gradeLetter = 'F'

        return `${gradeAvg.toFixed(2)}% ${gradeLetter}`
    }, [studentGrades, isUnweightedAssignment])

    const downloadGradesCSV = () => {
        // Create CSV header with student name and assignment names
        const header = ['Student Name', ...assignments.map(a => a.title), "Student Class Grade"]
        
        // Create rows for each student with their grades
        const rows = students.map(student => {
            const grades = studentGrades[student.uuid].map(assignment => assignment?.grade ? `${assignment.grade}%` : '')
            return [student.name, ...grades, calculateStudentClassGrade(student)]
        })

        const classAverageRow = ["Class Average", ...assignments.map(assignment => calculateClassAverage(assignment) ?? ''), '']

        // Combine header and rows
        const csvContent = [header, ...rows, classAverageRow]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n')

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `${activeClass.className}-grades-${new Date().getFullYear()}-${(new Date().getMonth() + 1).toString().padStart(2, '0')}-${new Date().getDate().toString().padStart(2, '0')}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    if (loading || !assignments || loadingStudents) {
        return (
            <div className="d-table h-100 mx-auto">
                <div className="d-table-cell text-center align-middle">
                    <BeatLoader size={15} />
                </div>
            </div>
        )
    }

    return (
        <div className="mx-2">
            <Row className="align-items-center">
                <Col>
                    <h1 className="h2 text-start mt-3">Class Grades</h1>
                </Col>
                <Col className="vertical-center text-end">
                    <button 
                        className="btn btn-primary me-2"
                        onClick={downloadGradesCSV}
                        disabled={loading || loadingStudents || students.length === 0}
                    >
                        Export CSV
                    </button>
                    <div className="d-inline-block">
                        <Form.Select
                            aria-label="Results per page"
                            className="d-inline-block w-auto me-2"
                            onChange={(e) => setCount(Number(e.target.value))}
                            value={count}
                        >
                            <option value={5}>5 per page</option>
                            <option value={10}>10 per page</option>
                            <option value={20}>20 per page</option>
                    </Form.Select>
                    </div>
                    <div className="d-inline-block">
                        <Pagination className={`d-flex justify-content-end my-0 mx-0 fade ${assignments.length > count && "show"}`}>
                            {assignments.length > count * 2 && 
                            <Pagination.First onClick={() => setStartIndex(0)} disabled={startIndex === 0} />
                            }
                            <Pagination.Prev onClick={() => setStartIndex(startIndex - count)} disabled={startIndex === 0} />
                            {Array.from(Array(Math.ceil(assignments.length / count))).map((_,pageNum) => 
                                ((Math.abs((startIndex/count)-pageNum) < 2) && 
                                    <Pagination.Item onClick={() => setStartIndex(pageNum * count)} className={`${((pageNum) === Math.floor(startIndex/count)) && "active"}`} key={"page-" + pageNum}>{pageNum+1}</Pagination.Item>
                                )
                            )}
                            <Pagination.Next onClick={() => setStartIndex(startIndex + count)} disabled={startIndex + count >= assignments.length} />
                            {assignments.length > count * 2 && 
                                <Pagination.Last onClick={() => setStartIndex(Math.ceil(assignments.length / count) * count - count)} disabled={startIndex + count >= assignments.length} />
                            }
                        </Pagination>
                    </div>
                </Col>
            </Row>
            <Row className="my-3">
                <div className="d-flex justify-content-start align-items-center gap-2">
                    <CreateGradingPeriod classId={activeClass.id} />
                    <SelectGradingPeriods 
                        classId={activeClass.id} 
                        selectedPeriods={selectedPeriods}
                        onSelect={(periods) => {
                            setSelectedPeriods(periods)
                            localStorage.setItem(SELECTED_PERIODS_LOCAL_KEY, JSON.stringify(periods))
                            refreshPeriods()
                        }}
                        refreshAssignments={reloadAssignments} />
                </div>
            </Row>
            {!loadingGradingPeriods && 
            <Row className="my-3">
                {selectedPeriods.map(periodId => {
                    const period = gradingPeriods.find(p => p.id === periodId)
                    if (!period) return null
                    return (
                        <span key={period.id} className="badge bg-info me-2 w-auto"> {period.name} </span>
                    )
                })}
            </Row>}
            <GradeBookTable 
                assignments={assignments} 
                students={students} 
                startIndex={startIndex} 
                count={count}
                studentGrades={studentGrades}
                calculateClassAverage={calculateClassAverage}
                calculateStudentClassGrade={calculateStudentClassGrade}
                isUnweightedAssignment={isUnweightedAssignment} />
        </div>
    )
}
